import { useMemo } from 'react'
import {
  calculateEstAppreciation,
  calculateMonthlyMortgage,
  calculatorFns,
} from 'calculations'

import type { CalculatorAssumptions } from 'calculations'

export type Product = 'saver' | 'boost'

export type CalculatorPageContent = CalculatorAssumptions & {
  interestRateAcre: number
  chartHpaStandard: number
  chartHpaHigh: number
  chartHpaDown: number
  initialOwnershipTooltip?: string
  estAppreciationTooltip?: string
  monthlySavingsTooltip?: string
  closingCostsSavedTooltip?: string
  saleSavingsTooltip?: string
  totalValueTooltip?: string
  earlyOutFeeDescription?: string
  cashOutFeeDescription?: string
  homeListPriceInputHelpText?: string
  interestRateInputHelpText?: string
  salePriceOverListedInputHelpText?: string
  hoaFeesInputHelpText?: string
  ownershipInputHelpText?: string
  legalDisclaimer?: {
    json: Document
  }
  hpaDisclaimerText?: {
    json: any
  }
  atClosingHeader?: {
    json: any
  }
  valueShareDescription?: {
    json: any
  }
  saverProductSubheader?: {
    json: any
  }
  boostProductSubheader?: {
    json: any
  }
}

export const VALUE_SHARE_PERCENT = 0.05
export const DEFAULT_TERM_LENGTH = 3

const useAcreCalculator = ({
  interestRate,
  interestRateAcre,
  expectedClose,
  termLength = DEFAULT_TERM_LENGTH,
  hoaFees = 0,
  assumptions,
  market,
}: {
  interestRate: number
  interestRateAcre: number
  expectedClose: number
  termLength?: number
  hoaFees?: number
  assumptions: CalculatorAssumptions
  market?: string
}) => {
  const {
    calculateMortgagePmi,
    calculateInsurance,
    calculatePropertyTaxes,
    calculateProjectedHomeValue,
    calculateExpectedClosingCosts,
    calculateCumulativePrincipal,
    calculateEarlyOutFee,
    calculateCashOutFee,
    calculateMortgageTotalValue,
    calculateSaverEstAppreciation,
    calculateBoostEstAppreciation,
    calculateSaverMonthlyPayment,
    calculateBoostMonthlyPayment,
    calculateProductTotalValue,
    calculateProductTermSavings,
  } = useMemo(() => {
    return calculatorFns(assumptions)
  }, [assumptions])

  const months = termLength * 12

  const valueShare = VALUE_SHARE_PERCENT * expectedClose
  const loanBalance = expectedClose - valueShare
  const expectedClosingCosts = calculateExpectedClosingCosts(expectedClose)

  const projectedHomeValue = useMemo(
    () => calculateProjectedHomeValue(months, expectedClose),
    [months, expectedClose, calculateProjectedHomeValue]
  )

  const mortgageAppreciation = calculateEstAppreciation(
    projectedHomeValue,
    expectedClose
  )
  const mortgagePrincipal = calculateCumulativePrincipal(
    months,
    loanBalance,
    interestRate / 100
  )

  const mortgageTotalValue = calculateMortgageTotalValue(
    loanBalance,
    months,
    expectedClose,
    valueShare,
    interestRate / 100
  )

  const mortgageNetValue = mortgageTotalValue - valueShare

  const monthlyPaymentMortgage = calculateMonthlyMortgage(
    loanBalance,
    interestRate / 100
  )

  const monthlyPaymentMortgageForAcreBase = calculateMonthlyMortgage(
    loanBalance,
    interestRateAcre / 100
  )

  const pmiMortgage = calculateMortgagePmi(
    loanBalance,
    expectedClose,
    valueShare
  )
  const propertyTaxesMortgage =
    calculatePropertyTaxes(expectedClose, market) * -1
  const insuranceMortgage = calculateInsurance(expectedClose, 1) * -1
  const monthlyTotalMortgage =
    monthlyPaymentMortgage +
    pmiMortgage +
    propertyTaxesMortgage +
    insuranceMortgage +
    hoaFees

  const baseMonthlyPaymentAcre =
    monthlyPaymentMortgageForAcreBase +
    pmiMortgage +
    propertyTaxesMortgage +
    insuranceMortgage

  const saverMonthlyPayment =
    calculateSaverMonthlyPayment(baseMonthlyPaymentAcre) + hoaFees

  const boostMonthlyPayment =
    calculateBoostMonthlyPayment(baseMonthlyPaymentAcre) + hoaFees

  // Financing = discounted P&I (plus PMI), excluding tax, insurance and HOA.
  // The v3 layout composes each product's displayed monthly as
  // base + tax + insurance (+ HOA) with every part rounded independently,
  // mirroring the backend PaymentBlocks contract, so the calculator lands on the
  // same figure the listing serves. `saverMonthlyPayment` / `boostMonthlyPayment`
  // above stay as they were because the wealth projection is keyed off them.
  const acreFinancingBase = monthlyPaymentMortgageForAcreBase + pmiMortgage
  const saverFinancingBase = calculateSaverMonthlyPayment(acreFinancingBase)
  const boostFinancingBase = calculateBoostMonthlyPayment(acreFinancingBase)
  const mortgageFinancingBase = monthlyPaymentMortgage + pmiMortgage

  const earlyOutFee = calculateEarlyOutFee(expectedClose)
  const cashOutFee = calculateCashOutFee(projectedHomeValue)

  const saverAppreciation = calculateSaverEstAppreciation(
    projectedHomeValue,
    expectedClose
  )

  const saverTotalValueBeforeCashOutFee = calculateProductTotalValue(
    'saver',
    loanBalance,
    valueShare,
    months,
    expectedClose,
    monthlyTotalMortgage,
    saverMonthlyPayment - hoaFees,
    expectedClosingCosts,
    interestRate / 100,
    interestRateAcre / 100,
    market
  )

  // Net the exit fee out of the total so wealth created sums with the exit-fee
  // row shown in the comparisons. The pre-fee value backs the chart and the ad
  // feed, so those keep using ...BeforeCashOutFee. (Folds in #1239.)
  const saverTotalValue = saverTotalValueBeforeCashOutFee - cashOutFee

  const saverNetValue = saverTotalValue - valueShare

  // Savings excludes the exit fee, which is its own row in the comparisons.
  const saverTermSavings = calculateProductTermSavings(
    'saver',
    months,
    expectedClose,
    valueShare,
    saverTotalValueBeforeCashOutFee
  )

  const boostAppreciation = calculateBoostEstAppreciation(
    projectedHomeValue,
    expectedClose
  )
  const boostTotalValueBeforeCashOutFee = calculateProductTotalValue(
    'boost',
    loanBalance,
    valueShare,
    months,
    expectedClose,
    monthlyTotalMortgage,
    boostMonthlyPayment - hoaFees,
    expectedClosingCosts,
    interestRate / 100,
    interestRateAcre / 100,
    market
  )

  const boostTotalValue = boostTotalValueBeforeCashOutFee - cashOutFee

  const boostNetValue = boostTotalValue - valueShare

  // Savings excludes the exit fee, which is its own row in the comparisons.
  const boostTermSavings = calculateProductTermSavings(
    'boost',
    months,
    expectedClose,
    valueShare,
    boostTotalValueBeforeCashOutFee
  )

  const buyTransactionCosts = expectedClose * assumptions.buyTransactionCostRate
  const saleTransactionCosts =
    projectedHomeValue * assumptions.saleTransactionCostRate

  return {
    valueShare,
    mortgageAppreciation,
    mortgagePrincipal,
    monthlyTotalMortgage,
    mortgageTotalValue,
    mortgageNetValue,
    saverAppreciation,
    saverMonthlyPayment,
    saverTotalValue,
    saverNetValue,
    saverTermSavings,
    boostAppreciation,
    boostMonthlyPayment,
    boostTotalValue,
    boostNetValue,
    boostTermSavings,
    earlyOutFee,
    cashOutFee,
    buyTransactionCosts,
    saleTransactionCosts,
    monthlyPaymentMortgage,
    pmiMortgage,
    propertyTaxesMortgage,
    insuranceMortgage,
    saverFinancingBase,
    boostFinancingBase,
    mortgageFinancingBase,
  }
}

export default useAcreCalculator
